import Unauthorized from "@/layout/error/component/Unauthorized";
import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { PERMISSIONS } from "@/permissions/constant";
import { OUTREACH_COMMUNICATION_PERMISSIONS, OUTREACH_MANAGEMENT_PERMISSIONS, OUTREACH_SEQUENCE_PERMISSIONS } from "@/config/outreach";
// Interview Scheduling is disabled — commented out, not deleted. See PLAN
// doc "resilient-stirring-tulip".
// import { INTERVIEW_VIEW_PERMISSIONS } from "@/config/interviewScheduling";
import { MODULES } from "@/permissions/modules";
import CandidateStatusPage from "@/pages/admin/candidate-status";
import Login from "@/pages/auth/login";
import Home from "@/pages/user/home";
import UserApply from "@/pages/user/apply";
import JobListing from "@/pages/user/job-list";
import Dashboard from "@/pages/admin/dashboard";
import Signup from "@/pages/auth/signup";
import Job from "@/pages/admin/job";
import Candidate from "@/pages/admin/candidate";
import JobDetail from "@/pages/admin/job-detail";
import BulkUpload from "@/pages/admin/bulk-upload";
import JobDetails from "@/pages/user/job-detail";
import ConfirmDetails from "@/pages/user/confirm-details";
import CandidateAnalysis from "@/pages/admin/candidate-analysis";
import AdminStatistics from "@/pages/admin/statistics";
import AdminUsage from "@/pages/admin/usage";
import GoogleCallback from "@/pages/auth/google-callback";
import MicrosoftCallback from "@/pages/auth/microsoft-callback";
import ForgotPassword from "@/pages/auth/forgot-password";
import ResetPassword from "@/pages/auth/reset-password";
import GithubCallback from "@/pages/auth/github-callback";
import LinkedinCallback from "@/pages/auth/linkedin-callback";
import EditCandidateProfile from "@/pages/admin/candidate-analysis/edit-profile";
import ApplyToJob from "@/pages/admin/candidate-analysis/apply-to-job";
import Profile from "@/pages/admin/profile";
import SuperAdminDashboard from "@/pages/super-admin/dashboard";
import Tenant from "@/pages/super-admin/tenant";
import RolePage from "@/pages/admin/role";
import OrgSettings from "@/pages/admin/org-settings";
import UserPage from "@/pages/admin/user";
import ComplianceDashboard from "@/pages/admin/compliance/dashboard";
import ComplianceIntake from "@/pages/admin/compliance/intake";
import ComplianceChecklists from "@/pages/admin/compliance/checklists";
import ComplianceAnalytics from "@/pages/admin/compliance/analytics";
import ComplianceAuditLog from "@/pages/admin/compliance/audit-log";
import ComplianceDetail from "@/pages/admin/compliance/detail";
import CompliancePortal from "@/pages/user/compliance-portal";
import TenantDetails from "@/pages/super-admin/tenant-details";
import Permissions from "@/pages/super-admin/permissions";
// CV Formatting is disabled — commented out, not deleted. See PLAN doc
// "resilient-stirring-tulip".
// import ResumeTemplatesAccess from "@/pages/super-admin/resume-templates";
import DatabaseManagement from "@/pages/super-admin/database-management";
import SuperAdminsPage from "@/pages/super-admin/superadmins";
import AcceptInvite from "@/pages/auth/accept-invite";
import CandidateConfirmation from "@/pages/user/candidate-confirmation";
// Interview Scheduling is disabled — commented out, not deleted. See PLAN
// doc "resilient-stirring-tulip".
// import InterviewRsvp from "@/pages/user/interview-rsvp";
import ResendConfirmation from "@/pages/user/candidate-confirmation/ResendConfirmation";
import BulkExtractionReview from "@/pages/admin/bulk-upload/BulkExtractionReivew";
import BulkIntakeConfirmation from "@/pages/admin/bulk-upload/BulkIntakeConfirmation";
import BulkIntakeConfirmationResend from "@/pages/admin/bulk-upload/BulkIntakeConfirmationResend";
import ReuploadResume from "@/pages/admin/candidate-analysis/reupload-resume";
import BrandingPage from "@/pages/admin/branding";
import AuditLogsDashboard from "@/pages/super-admin/audit-logs";
import SuperAdminUsage from "@/pages/super-admin/usage";
import PlatformSettings from "@/pages/super-admin/platform-settings";
import FeedbackQueue from "@/pages/super-admin/feedback";
import MyFeedback from "@/pages/admin/my-feedback";
// Sourcing (and its child, Extension) is disabled — commented out, not
// deleted. See PLAN doc "resilient-stirring-tulip".
// import PlatformSourcingKeysPage from "@/pages/super-admin/sourcing-keys";
// import ConfigureSourcingApisPage from "@/pages/admin/sourcing-apis";
// import ChromeExtension from "@/pages/admin/chrome-extension";
import RecordLookup from "@/pages/admin/record-lookup";
import SuperAdminRecordLookup from "@/pages/super-admin/record-lookup";
// import Campaigns from "@/pages/admin/campaigns";
// import CampaignNew from "@/pages/admin/campaigns/new";
// import CampaignDetail from "@/pages/admin/campaigns/detail";
// import Shortlist from "@/pages/admin/shortlist";

// Phase 1 — Sourcing

// Phase 2 — Outreach. Keep the collaboration-heavy modules out of the initial
// admin bundle until a recruiter actually opens an outreach route.
const Sequences = lazy(() => import("@/pages/admin/sequences"));
const SequenceNew = lazy(() => import("@/pages/admin/sequences/new"));
const SequenceDetail = lazy(() => import("@/pages/admin/sequences/detail"));
const OutreachSettings = lazy(() => import("@/pages/admin/outreach-settings"));
const Communications = lazy(() => import("@/pages/admin/communications"));
// Interview Scheduling is disabled — commented out, not deleted. See PLAN
// doc "resilient-stirring-tulip".
// const InterviewScheduling = lazy(() => import("@/pages/admin/interview-scheduling"));

// For pages that fill the viewport and manage their own internal scrolling —
// Inbox and Communications are two-pane apps whose panes scroll independently,
// so the route must NOT grow with content.
const outreachRoute = (Page) => (
  <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-text-secondary">Loading outreach…</div>}>
    <div className="h-full min-h-0 overflow-hidden">
      <Page />
    </div>
  </Suspense>
);

// For pages built on SplitReviewLayout (bulk review, edit profile, reupload
// resume, apply-to-job): a form pane and a resume pane side by side, each
// scrolling on its own. Only at `lg` — SplitReviewLayout stacks the panes at
// 70vh below it and lets the admin layout's own pane scroll between them, so
// the wrapper must not force `overflow-hidden` there the way `outreachRoute`
// does, or mobile loses its only scrollbar.
const splitReviewRoute = (Page) => (
  <div className="lg:flex lg:h-full lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden">
    <Page />
  </div>
);

// For lazily-loaded pages that are ordinary lists: they grow with their content
// and the admin layout's own `overflow-y-auto` pane does the scrolling, exactly
// as it does for the eagerly-imported list pages (candidate, job, user, role),
// which are rendered with no wrapper at all.
//
// Interview Scheduling was on `outreachRoute`, and that wrapper's
// `h-full overflow-hidden` silently clipped it: at 50 rows per page the content
// measured 2937px inside an 855px box, so roughly two thousand pixels of rows
// were cut off with no way to reach them. It read as "scrolling is broken",
// and nothing in the page or the table could have fixed it — the clip is two
// levels above both.
const listRoute = (Page) => (
  <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-text-secondary">Loading…</div>}>
    <Page />
  </Suspense>
);

/**
 * Route Configuration
 *
 * @property {string} path - URL path
 * @property {ReactNode} element - Component to render
 * @property {boolean} isAuth - Whether route requires authentication
 * @property {string} [module] - Module code the route belongs to. Not
 *   entitled renders Unauthorized; merely unavailable loads normally, because
 *   a kill switch stops new work and never a read.
 *
 * A `module` here does nothing on its own. `routes/index.jsx` has to
 * destructure it and pass it to `PermissionGuard`, and `PermissionGuard` has
 * to act on it. All three edits, or the key is silently dropped.
 */

export const routeConfig = [
  // Auth Routes
  {
    path: "/login",
    element: <Login />,
    isAuth: false,
  },
  {
    path: "/signup",
    element: <Signup />,
    isAuth: false,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    isAuth: false,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    isAuth: false,
  },
  {
    path: "/auth/accept-invite",
    element: <AcceptInvite />,
    isAuth: false,
  },

  // User Routes
  {
    path: "/",
    element: <Home />,
    isAuth: false,
  },
  {
    path: "/user/job-listing",
    element: <JobListing />,
    isAuth: false,
  },
  {
    path: "/user/job-detail/:jobId",
    element: <JobDetails />,
    isAuth: false,
  },
  {
    path: "/user/apply/:jobId",
    element: <UserApply />,
    isAuth: false,
  },
  {
    path: "/user/confirm-details/:candidateId",
    element: <ConfirmDetails />,
    isAuth: false,
  },
  {
    path: "/user/candidate-confirm",
    element: <CandidateConfirmation />,
    isAuth: false,
  },
  {
    path: "/user/candidate-confirm/resend",
    element: <ResendConfirmation />,
    isAuth: false,
  },
  // Interview Scheduling is disabled — commented out, not deleted. See
  // PLAN doc "resilient-stirring-tulip".
  // {
  //   // Candidate RSVP for an interview invite. Public: the RSVP token in the
  //   // URL is the credential — no login, no tenant slug needed (the token
  //   // itself resolves the tenant server-side; see interview/rsvp_dependencies.py).
  //   path: "/interview-rsvp",
  //   element: <InterviewRsvp />,
  //   isAuth: false,
  // },
  {
    // Candidate self-service compliance upload portal (Phase K, D10). Public:
    // the URL token is the credential — no login, no tenant slug needed.
    path: "/compliance-portal/:token",
    element: <CompliancePortal />,
    isAuth: false,
  },

  // OAuth Callback Routes
  {
    path: "/auth/google/callback",
    element: <GoogleCallback />,
    isAuth: false,
  },
  {
    path: "/auth/microsoft/callback",
    element: <MicrosoftCallback />,
    isAuth: false,
  },
  {
    path: "/auth/github/callback",
    element: <GithubCallback />,
    isAuth: false,
  },
  {
    path: "/auth/linkedin/callback",
    element: <LinkedinCallback />,
    isAuth: false,
  },

  // Admin Routes
  // Admin
  { path: "/admin/dashboard", element: <Dashboard />, isAuth: true },
  { path: "/admin/jobs", element: <Job />, isAuth: true },
  { path: "/admin/candidates", element: <Candidate />, isAuth: true },
  { path: "/admin/candidate-analysis/:candidateId", element: <CandidateAnalysis />, isAuth: true },

  // Phase 2 — Outreach
  { path: "/admin/sequences", element: outreachRoute(Sequences), isAuth: true, permissions: OUTREACH_SEQUENCE_PERMISSIONS, module: MODULES.OUTREACH },
  { path: "/admin/sequences/new", element: outreachRoute(SequenceNew), isAuth: true, permissions: [PERMISSIONS.OUTREACH_MANAGE], module: MODULES.OUTREACH },
  { path: "/admin/sequences/:sequenceId", element: outreachRoute(SequenceDetail), isAuth: true, permissions: OUTREACH_SEQUENCE_PERMISSIONS, module: MODULES.OUTREACH },
  // An ordinary scrolling page, so `listRoute` and not `outreachRoute`: the
  // latter clips anything taller than the viewport (see its comment above).
  { path: "/admin/outreach-settings", element: listRoute(OutreachSettings), isAuth: true, permissions: OUTREACH_MANAGEMENT_PERMISSIONS, module: MODULES.OUTREACH },
  // The two pages this one replaced. Their guards are unchanged, so a caller
  // without the permission or the module still gets Unauthorized rather than
  // a redirect to a page they cannot open.
  {
    path: "/admin/templates",
    element: <Navigate to="/admin/outreach-settings?tab=templates" replace />,
    isAuth: true,
    permissions: OUTREACH_MANAGEMENT_PERMISSIONS,
    module: MODULES.OUTREACH,
  },
  {
    path: "/admin/style-guides",
    element: <Navigate to="/admin/outreach-settings?tab=tone" replace />,
    isAuth: true,
    permissions: OUTREACH_MANAGEMENT_PERMISSIONS,
    module: MODULES.OUTREACH,
  },
  // "Message Log" is the Communication Center's Table view now. Same guards as
  // the page it points at, for the same reason as the two above.
  {
    path: "/admin/inbox",
    element: <Navigate to="/admin/communications?view=table" replace />,
    isAuth: true,
    permissions: OUTREACH_COMMUNICATION_PERMISSIONS,
    module: MODULES.OUTREACH,
  },
  { path: "/admin/communications", element: outreachRoute(Communications), isAuth: true, permissions: OUTREACH_COMMUNICATION_PERMISSIONS, module: MODULES.OUTREACH },
  // Interview Scheduling is disabled — commented out, not deleted. See
  // PLAN doc "resilient-stirring-tulip".
  // {
  //   path: "/admin/interview-scheduling",
  //   element: listRoute(InterviewScheduling),
  //   isAuth: true,
  //   module: MODULES.INTERVIEW_SCHEDULING,
  //   permissions: INTERVIEW_VIEW_PERMISSIONS,
  // },
  {
    path: "/admin/unauthorized",
    element: <Unauthorized />,
    isAuth: true,
    permissions: null,
  },
  {
    path: "/admin/dashboard",
    element: <Dashboard />,
    isAuth: true,
    permissions: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.CANDIDATE_VIEW, PERMISSIONS.JOB_VIEW],
  },
  {
    // Admin Statistics — diagnostic recruitment analytics.
    path: "/admin/statistics",
    element: <AdminStatistics />,
    isAuth: true,
    permissions: [PERMISSIONS.STATISTICS_VIEW],
  },
  {
    // Usage metering — what this tenant has consumed. `usage.view` opens the
    // totals; the per-user breakdown inside additionally needs
    // `usage.view_users`, so either permission gets you onto the page.
    path: "/admin/usage",
    element: <AdminUsage />,
    isAuth: true,
    permissions: [PERMISSIONS.USAGE_VIEW, PERMISSIONS.USAGE_VIEW_USERS],
  },
  {
    path: "/admin/jobs",
    element: <Job />,
    isAuth: true,
    permissions: [PERMISSIONS.JOB_VIEW, PERMISSIONS.JOB_CREATE, PERMISSIONS.JOB_UPDATE, PERMISSIONS.JOB_DELETE],
  },
  {
    path: "/admin/job-detail/:jobId",
    element: <JobDetail />,
    isAuth: true,
    permissions: [PERMISSIONS.JOB_VIEW],
  },
  {
    path: "/admin/job-detail/:jobId/bulk-upload",
    element: <BulkUpload />,
    module: MODULES.BULK_INTAKE,
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_CREATE],
  },
  {
    path: "/admin/job-detail/:jobId/bulk-review/:batchId",
    element: splitReviewRoute(BulkExtractionReview),
    module: MODULES.BULK_INTAKE,
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_CREATE],
  },
  {
    path: "/recruiter/bulk-confirm",
    element: <BulkIntakeConfirmation />,
    isAuth: false, // token itself is the credential, same as CandidateConfirmation
  },
  {
    path: "/recruiter/bulk-confirm/resend",
    element: <BulkIntakeConfirmationResend />,
    isAuth: false,
  },
  {
    path: "/admin/candidates",
    element: <Candidate />,
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_VIEW, PERMISSIONS.CANDIDATE_CREATE],
  },
  {
    path: "/admin/candidate-analysis/:candidateId",
    element: <CandidateAnalysis />,
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_VIEW],
  },
  {
    path: "/admin/edit-candidate-profile/:candidateId",
    element: splitReviewRoute(EditCandidateProfile),
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_UPDATE],
  },
  {
    path: "/admin/role",
    element: <RolePage />,
    isAuth: true,
    permissions: [PERMISSIONS.ROLE_VIEW, PERMISSIONS.ROLE_CREATE, PERMISSIONS.ROLE_UPDATE, PERMISSIONS.ROLE_DELETE, PERMISSIONS.ROLE_MANAGE],
  },
  {
    path: "/admin/user",
    element: <UserPage />,
    isAuth: true,
    permissions: [PERMISSIONS.USER_VIEW, PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_DELETE],
  },
  {
    path: "/admin/user-profile",
    element: <Profile />,
    isAuth: true,
  },
  {
    path: "/admin/branding",
    element: <BrandingPage />,
    module: MODULES.BRANDING,
    isAuth: true,
    permissions: [PERMISSIONS.BRANDING_VIEW, PERMISSIONS.BRANDING_MANAGE],
  },
  {
    // The feedback this user sent, and where each one stands.
    path: "/admin/my-feedback",
    element: <MyFeedback />,
    module: MODULES.FEEDBACK,
    isAuth: true,
    permissions: [PERMISSIONS.FEEDBACK_SUBMIT],
  },
  {
    path: "/admin/org-settings",
    element: <OrgSettings />,
    isAuth: true,
    permissions: [PERMISSIONS.TENANT_CONFIG_VIEW, PERMISSIONS.TENANT_CONFIG_EDIT],
  },
  // Sourcing (Campaigns + Shortlist) and its child, Extension, are
  // disabled — commented out, not deleted. See PLAN doc
  // "resilient-stirring-tulip".
  // {
  //   path: "/admin/chrome-extension",
  //   element: <ChromeExtension />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.EXTENSION_VIEW],
  //   module: MODULES.EXTENSION,
  // },
  // // Sourcing (Campaigns + Shortlist). /new is registered before
  // // /:campaignId so the param route doesn't capture it.
  // {
  //   path: "/admin/campaigns",
  //   element: <Campaigns />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.CAMPAIGN_VIEW],
  //   module: MODULES.SOURCING,
  // },
  // {
  //   path: "/admin/campaigns/new",
  //   element: <CampaignNew />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.CAMPAIGN_MANAGE],
  //   module: MODULES.SOURCING,
  // },
  // {
  //   path: "/admin/campaigns/:campaignId",
  //   element: <CampaignDetail />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.CAMPAIGN_VIEW],
  //   module: MODULES.SOURCING,
  // },
  // {
  //   // This tenant's own sourcing API credentials. Gated on the permission here,
  //   // and additionally on the SuperAdmin entitlement inside the page — a tenant
  //   // using the platform's keys sees an explanation instead of a form. The API
  //   // enforces both regardless.
  //   path: "/admin/sourcing-apis",
  //   element: <ConfigureSourcingApisPage />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.CAMPAIGN_CREDENTIALS_MANAGE],
  //   module: MODULES.SOURCING,
  // },
  // {
  //   path: "/admin/shortlist",
  //   element: <Shortlist />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.CAMPAIGN_VIEW],
  //   module: MODULES.SOURCING,
  // },
  // Compliance.
  // Phase H (D7/D8): Compliance is its own sidebar section with a Dashboard
  // landing and a Document Intake list; Checklists + detail/review stay under it.
  // All static paths are registered before /admin/compliance/:id so they aren't
  // captured by the param route (§9e capture gotcha).
  {
    // Dashboard landing (Phase H; real data extended in Phase I).
    path: "/admin/compliance",
    element: <ComplianceDashboard />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_VIEW],
  },
  {
    // Document Intake — the relocated compliance-candidate list (D8).
    path: "/admin/compliance/intake",
    element: <ComplianceIntake />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_VIEW],
  },
  {
    path: "/admin/compliance/checklists",
    element: <ComplianceChecklists />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_MANAGE],
  },
  {
    // Analytics (Phase 2B). Leadership metrics page; VIEW-gated like the
    // dashboard. Static path, registered before :id so it isn't captured.
    path: "/admin/compliance/analytics",
    element: <ComplianceAnalytics />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_VIEW],
  },
  {
    // The tenant's own compliance audit trail. Its own permission rather than
    // COMPLIANCE_VIEW: the trail is tenant-wide with no data-visibility
    // narrowing, so an administrator grants it deliberately. Static path, so
    // registered before :id below.
    path: "/admin/compliance/audit-log",
    element: <ComplianceAuditLog />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_AUDIT_VIEW],
  },
  {
    // Detail/review screen (Phase E). Registered after the static compliance
    // paths so they aren't captured by :id. Document Intake row-click targets it.
    path: "/admin/compliance/:id",
    element: <ComplianceDetail />,
    module: MODULES.COMPLIANCE,
    isAuth: true,
    permissions: [PERMISSIONS.COMPLIANCE_REVIEW],
  },

  // The signed-in person's own profile (URL without /admin prefix)
  {
    // No permission gate on purpose. This is the signed-in person's own
    // profile, and it is FALLBACK_HOME in routes/landing.js — the destination
    // for an account that holds none of the landing permissions. Gating it
    // would send exactly those accounts to "Unauthorized", which is the
    // outcome landing.js exists to prevent.
    path: "/user-profile",
    element: <Profile />,
    isAuth: true,
  },
  {
    path: "/admin/candidate-analysis/:candidateId/reupload-resume",
    element: splitReviewRoute(ReuploadResume),
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_UPDATE],
  },
  {
    path: "/user/resume-reupload-confirm",
    element: <CandidateConfirmation flow="resume-reupload" />,
    isAuth: false,
  },
  {
    path: "/user/resume-reupload-confirm/resend",
    element: <ResendConfirmation flow="resume-reupload" />,
    isAuth: false,
  },
  {
    path: "/admin/candidate-analysis/:candidateId/apply-to-job/:jobId",
    element: splitReviewRoute(ApplyToJob),
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    isAuth: true,
    permissions: [PERMISSIONS.CANDIDATE_UPDATE],
  },
  {
    path: "/user/jobfit-apply-confirm",
    element: <CandidateConfirmation flow="jobfit-apply" />,
    isAuth: false, // token itself is the credential, same as CandidateConfirmation
  },

  {
    path: "/admin/candidate-status",
    element: <CandidateStatusPage />,
    isAuth: true,
    permissions: [
      PERMISSIONS.CANDIDATE_STATUS_VIEW,
      PERMISSIONS.CANDIDATE_STATUS_CREATE,
      PERMISSIONS.CANDIDATE_STATUS_UPDATE,
      PERMISSIONS.CANDIDATE_STATUS_DELETE,
    ],
  },

  // Super admin router
  {
    path: "/super-admin/dashboard",
    element: <SuperAdminDashboard />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/tenant",
    element: <Tenant />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/user-profile",
    element: <Profile />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/tenant-details/:tenantId?",
    element: <TenantDetails />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/permissions",
    element: <Permissions />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  // CV Formatting is disabled — commented out, not deleted. See PLAN doc
  // "resilient-stirring-tulip".
  // {
  //   path: "/super-admin/resume-templates",
  //   element: <ResumeTemplatesAccess />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  // },
  {
    path: "/super-admin/database-management",
    element: <DatabaseManagement />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/superadmins",
    element: <SuperAdminsPage />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/audit-logs",
    element: <AuditLogsDashboard />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    // Cross-tenant usage comparison. Separate from /admin/usage because
    // RouteGuard hard-redirects every SuperAdmin away from /admin/* — the
    // duplication is forced by existing routing, not chosen.
    path: "/super-admin/usage",
    element: <SuperAdminUsage />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    // Fleet-wide module availability (the kill switch) and platform-wide
    // guardian policy.
    path: "/super-admin/platform-settings",
    element: <PlatformSettings />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    // Product feedback from every tenant. Not module-gated: withholding the
    // module hides a tenant's form, never what it already sent.
    path: "/super-admin/feedback",
    element: <FeedbackQueue />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  // Sourcing is disabled — commented out, not deleted. See PLAN doc
  // "resilient-stirring-tulip".
  // {
  //   // Platform-owned sourcing API keys. SuperAdmin-only: these are OUR keys,
  //   // shared by every tenant that does not bring its own.
  //   path: "/super-admin/sourcing-keys",
  //   element: <PlatformSourcingKeysPage />,
  //   isAuth: true,
  //   permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  // },
  {
    // Record lookup — GDPR / EU AI Act dossier. No module gate: a withheld
    // module must never be the reason a tenant cannot answer a regulator.
    // `record.view` opens search and the dossier; `record.export` (with
    // `record.view`) opens the download.
    path: "/admin/record-lookup",
    element: <RecordLookup />,
    isAuth: true,
    permissions: [PERMISSIONS.RECORD_VIEW],
  },
  {
    path: "/super-admin/record-lookup",
    element: <SuperAdminRecordLookup />,
    isAuth: true,
    permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/user",
    element: <UserPage />,
    isAuth: true,
    permissions: [PERMISSIONS.USER_VIEW, PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_DELETE, PERMISSIONS.SUPER_ADMIN_MANAGE],
  },
  {
    path: "/super-admin/role",
    element: <RolePage />,
    isAuth: true,
    permissions: [
      PERMISSIONS.ROLE_VIEW,
      PERMISSIONS.ROLE_CREATE,
      PERMISSIONS.ROLE_UPDATE,
      PERMISSIONS.ROLE_DELETE,
      PERMISSIONS.ROLE_MANAGE,
      PERMISSIONS.SUPER_ADMIN_MANAGE,
    ],
  },
];
